import { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Bar, LineChart, Line, Legend, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';

const LINE_COLORS = [
  '#6366F1', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#3B82F6', '#84CC16',
];

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Date ↓' },
  { value: 'date_asc', label: 'Date ↑' },
  { value: 'handle_asc', label: 'Handle A–Z' },
  { value: 'handle_desc', label: 'Handle Z–A' },
];

const CUTOFF_OPTIONS = [
  { value: '1d', label: '1d' }, { value: '2d', label: '2d' }, { value: '3d', label: '3d' },
  { value: '4d', label: '4d' }, { value: '5d', label: '5d' }, { value: '6d', label: '6d' },
  { value: '7d', label: '7d' }, { value: '14d', label: '14d' },
  { value: '1mo', label: '1 month' }, { value: 'all', label: 'All' },
];

const CUTOFF_DAYS = { '1d':1,'2d':2,'3d':3,'4d':4,'5d':5,'6d':6,'7d':7,'14d':14,'1mo':30,'all':null };

const TZ_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Zurich', label: 'Zurich' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Asia/Bangkok', label: 'Bangkok' },
];

const CLUSTER_SORT_OPTIONS = [
  { value: 'volume', label: 'Volume' },
  { value: 'activity', label: 'Recent' },
  { value: 'alpha', label: 'A–Z' },
];

function fmtDate(s, tz) {
  if (!s) return '—';
  const d = new Date(s + (s.includes('Z') || s.includes('+') ? '' : 'Z'));
  return d.toLocaleString('en-US', { timeZone: tz, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
}

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 0) return 'future';
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

function Flag({ label, active }) {
  return (
    <span className={`inline-block w-5 text-center text-xs font-bold rounded ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
      {label}
    </span>
  );
}

// W flag has 3 states: 0=gray(unseen), 1=red(rejected), 2=green(posted)
function WalterFlag({ value }) {
  const v = Number(value);
  const styles = {
    0: 'bg-gray-100 text-gray-400',
    1: 'bg-red-100 text-red-600',
    2: 'bg-green-100 text-green-700',
  };
  const titles = { 0: 'Not seen by Walter', 1: 'Rejected', 2: 'Drafted/Posted' };
  return (
    <span title={titles[v] || ''} className={`inline-block w-5 text-center text-xs font-bold rounded ${styles[v] || styles[0]}`}>
      W
    </span>
  );
}

function ActivityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const relevant = payload.find(p => p.dataKey === 'relevant')?.value || 0;
  const skipped = payload.find(p => p.dataKey === 'skipped')?.value || 0;
  const total = relevant + skipped;
  const pct = total > 0 ? ((relevant / total) * 100).toFixed(1) : '0.0';
  return (
    <div className="bg-white border rounded shadow-sm p-2 text-xs">
      <div className="font-medium mb-1">{label}</div>
      <div style={{ color: '#10B981' }}>Relevant: {relevant}</div>
      <div style={{ color: '#9CA3AF' }}>Skipped: {skipped}</div>
      <div className="border-t mt-1 pt-1">{pct}% relevant</div>
    </div>
  );
}

export default function KBPage() {
  const [tab, setTab] = useState('feed');
  const [tz, setTz] = useState(() => localStorage.getItem('kb_tz') || 'Asia/Dubai');

  // Feed state
  const [tweets, setTweets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState(() => localStorage.getItem('kb_feed_sort') || 'date_desc');
  const [handle, setHandle] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');

  // Stats
  const [stats, setStats] = useState(null);

  // Clusters
  const [clusters, setClusters] = useState([]);
  const [clusterTweetIds, setClusterTweetIds] = useState(null);
  const [activeClusterLabel, setActiveClusterLabel] = useState('');
  const [clusterSort, setClusterSort] = useState(() => localStorage.getItem('kb_cluster_sort') || 'volume');
  const [clusterCutoff, setClusterCutoff] = useState(() => localStorage.getItem('kb_cluster_cutoff') || 'all');

  // Activity (combined)
  const [activityDays, setActivityDays] = useState('7');
  const [showIrrelevant, setShowIrrelevant] = useState(true);
  const [combinedActivity, setCombinedActivity] = useState([]);
  const [activityTotals, setActivityTotals] = useState({ relevant: 0, skipped: 0 });

  // Authors tab
  const [authors, setAuthors] = useState([]);
  const [authorSort, setAuthorSort] = useState('total'); // 'total' | 'relevant_pct' | 'handle'

  // Cluster growth chart
  const [growthTopN, setGrowthTopN] = useState(5);
  const [growthDays, setGrowthDays] = useState(30);
  const [growthData, setGrowthData] = useState([]);
  const [clusterView, setClusterView] = useState("cumulative"); // "cumulative" | "activity6h"
  const [chartSort, setChartSort] = useState('volume'); // 'volume' | 'recent'
  const [activity6hData, setActivity6hData] = useState([]);

  // Auto-refresh
  const [countdown, setCountdown] = useState(30);
  const debounceRef = useRef(null);

  // Tz persist
  useEffect(() => { localStorage.setItem('kb_tz', tz); }, [tz]);
  useEffect(() => { localStorage.setItem('kb_feed_sort', sort); }, [sort]);
  useEffect(() => { localStorage.setItem('kb_cluster_sort', clusterSort); }, [clusterSort]);
  useEffect(() => { localStorage.setItem('kb_cluster_cutoff', clusterCutoff); }, [clusterCutoff]);

  // Fetch tweets
  const fetchTweets = useCallback(async (p = page) => {
    const params = new URLSearchParams({ page: p, sort });
    if (handle) params.set('handle', handle);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (q) params.set('q', q);
    if (!showIrrelevant) params.set('relevant', '1');
    if (clusterTweetIds) params.set('tweet_ids', clusterTweetIds.join(','));
    try {
      const res = await fetch('/api/kb/tweets?' + params);
      const data = await res.json();
      setTweets(data.tweets);
      setTotal(data.total);
      setTotalPages(data.pages);
      setPage(data.page);
    } catch { /* ignore */ }
  }, [page, sort, handle, from, to, q, showIrrelevant, clusterTweetIds]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/kb/stats');
      setStats(await res.json());
    } catch { /* ignore */ }
  }, []);

  // Fetch clusters
  const fetchClusters = useCallback(async () => {
    try {
      const res = await fetch('/api/kb/clusters');
      setClusters(await res.json());
    } catch { /* ignore */ }
  }, []);

  // Fetch authors
  const fetchAuthors = useCallback(async () => {
    try {
      const res = await fetch('/api/kb/authors');
      setAuthors(await res.json());
    } catch { /* ignore */ }
  }, []);

  // Fetch combined activity
  const fetchCombinedActivity = useCallback(async (days) => {
    try {
      const res = await fetch(`/api/kb/activity-combined?days=${days}`);
      const json = await res.json();
      setCombinedActivity(json.buckets || []);
      setActivityTotals({ relevant: json.total_relevant, skipped: json.total_skipped });
    } catch (e) { console.error('fetchCombinedActivity error', e); }
  }, []);

  // Fetch cluster activity 6h
  const fetchActivity6h = useCallback(async (top, days, sort = 'volume') => {
    try {
      const res = await fetch(`/api/kb/cluster-activity-6h?top=${top}&days=${days}&sort=${sort}`);
      const json = await res.json();
      setActivity6hData(json.clusters || []);
    } catch (e) { console.error('fetchActivity6h error', e); }
  }, []);

  // Fetch cluster growth
  const fetchClusterGrowth = useCallback(async (top, days, sort = 'volume') => {
    const params = new URLSearchParams({ top, sort });
    if (days !== 'all') params.set('days', days);
    else params.set('days', 'all');
    const res = await fetch(`/api/kb/cluster-growth?${params}`);
    const json = await res.json();
    setGrowthData(json.clusters || []);
  }, []);

  // Initial load & auto-refresh
  useEffect(() => {
    fetchTweets(page);
    fetchStats();
    fetchClusters();
    fetchCombinedActivity('7');
    fetchClusterGrowth(5, 30, 'volume');
    fetchActivity6h(5, 30, 'volume');
  }, []);

  // Fetch authors when tab is selected
  useEffect(() => {
    if (tab === 'authors') fetchAuthors();
  }, [tab, fetchAuthors]);

  // Refetch cluster growth when params change
  useEffect(() => {
    fetchClusterGrowth(growthTopN, growthDays, chartSort);
    fetchActivity6h(growthTopN, growthDays === 'all' ? 30 : growthDays, chartSort);
  }, [growthTopN, growthDays, chartSort]);

  // Refetch tweets when filters/sort/page/cluster change
  useEffect(() => {
    fetchTweets(1);
    setPage(1);
  }, [clusterTweetIds]);

  useEffect(() => {
    fetchTweets(page);
  }, [sort, page, showIrrelevant]);

  // Debounced fetch on text filter change
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchTweets(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [handle, from, to, q]);

  // Refetch combined activity when days changes
  useEffect(() => {
    fetchCombinedActivity(activityDays);
  }, [activityDays, fetchCombinedActivity]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTweets(page);
      fetchStats();
      fetchClusters();
      fetchCombinedActivity(activityDays);
      setCountdown(30);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTweets, fetchStats, fetchClusters, fetchCombinedActivity, activityDays, page]);

  // Countdown tick
  useEffect(() => {
    setCountdown(30);
    const tick = setInterval(() => setCountdown(c => (c <= 1 ? 30 : c - 1)), 1000);
    return () => clearInterval(tick);
  }, []);

  // Sort/filter clusters client-side
  const filteredClusters = (() => {
    let list = [...clusters];
    const days = CUTOFF_DAYS[clusterCutoff];
    if (days !== null) {
      // eslint-disable-next-line react-hooks/purity
      const cutoffMs = Date.now() - days * 86400000;
      list = list.filter(c => c.last_activity && new Date(c.last_activity).getTime() >= cutoffMs);
    }
    if (clusterSort === 'volume') list.sort((a, b) => (b.tweet_count || 0) - (a.tweet_count || 0));
    else if (clusterSort === 'activity') list.sort((a, b) => (b.last_activity || '').localeCompare(a.last_activity || ''));
    else list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return list;
  })();

  const filterByHandle = (h) => {
    setHandle(h);
    setPage(1);
    setTab('feed');
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">KB Viewer</h1>
          <div className="flex gap-1">
            {[
              { id: 'feed', label: 'Feed' },
              { id: 'activity', label: 'Activity 📊' },
              { id: 'cluster-chart', label: 'Clusters 📈' },
              { id: 'clusters', label: 'Clusters' },
              { id: 'authors', label: 'Authors 👤' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1 rounded text-sm font-medium ${tab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >{t.label}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>Refresh in {countdown}s</span>
          <select
            value={tz}
            onChange={e => setTz(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
          >
            {TZ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="flex gap-4 mb-4 text-sm text-gray-600 flex-wrap">
          <span><strong>{stats.total_tweets}</strong> tweets ({stats.relevant_tweets} relevant, {stats.irrelevant_tweets} irrelevant)</span>
          <span><strong>{stats.unprocessed_facts}</strong> unprocessed facts</span>
          <span><strong>{stats.unprocessed_cluster}</strong> unprocessed clusters</span>
          {stats.last_received_at && <span>Last received: {fmtDate(stats.last_received_at, tz)}</span>}
        </div>
      )}

      {tab === 'activity' && (
        <>
          <div className="flex gap-2 mb-3 items-center">
            <span className="text-xs text-gray-500">Period:</span>
            {[{ label: '24h', value: '1' }, { label: '7d', value: '7' }, { label: '30d', value: '30' }, { label: 'ALL', value: 'all' }].map(o => (
              <button
                key={o.value}
                onClick={() => setActivityDays(o.value)}
                className={`px-2 py-1 rounded text-xs font-medium border ${activityDays === o.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              >{o.label}</button>
            ))}
            <button
              onClick={() => setShowIrrelevant(v => !v)}
              className={`ml-auto px-2 py-1 rounded text-xs font-medium border ${showIrrelevant ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300'}`}
            >Show irrelevant</button>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            {combinedActivity.length === 0 ? (
              <div className="text-center text-gray-400 text-sm py-8">No activity data</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={combinedActivity} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <XAxis
                    dataKey="bucket"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    interval={Math.max(0, Math.floor(combinedActivity.length / 12))}
                    tickFormatter={v => {
                      const parts = v.split(' ');
                      const d = new Date(parts[0] + 'T00:00:00Z');
                      const label = d.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                      return parts[1] && activityDays !== '30' && activityDays !== 'all' ? `${label} ${parts[1]}` : label;
                    }}
                  />
                  <YAxis tick={{ fontSize: 11 }} width={32} allowDecimals={false} />
                  <Tooltip content={<ActivityTooltip />} />
                  {combinedActivity.filter(d => {
                    const day = new Date((d.bucket.split(' ')[0]) + 'T00:00:00Z').getUTCDay();
                    return day === 0 || day === 6;
                  }).map(d => (
                    <ReferenceArea key={d.bucket} x1={d.bucket} x2={d.bucket} fill="#F3F4F6" fillOpacity={0.8} />
                  ))}
                  <Bar dataKey="relevant" stackId="a" fill="#10B981" name="Relevant" radius={[0,0,0,0]} maxBarSize={24} cursor="pointer" onClick={(d) => {
                    if (!d?.bucket) return;
                    const dateStr = d.bucket.split(' ')[0];
                    setFrom(dateStr); setTo(dateStr); setTab('feed'); setPage(1);
                  }} />
                  {showIrrelevant && (
                    <Bar dataKey="skipped" stackId="a" fill="#9CA3AF" name="Skipped" radius={[2,2,0,0]} maxBarSize={24} cursor="pointer" onClick={(d) => {
                      if (!d?.bucket) return;
                      const dateStr = d.bucket.split(' ')[0];
                      setFrom(dateStr); setTo(dateStr); setTab('feed'); setPage(1);
                    }} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {activityTotals.relevant} relevant · {activityTotals.skipped} skipped ·{' '}
            {((activityTotals.relevant / (activityTotals.relevant + activityTotals.skipped || 1)) * 100).toFixed(1)}% relevant
          </div>
        </>
      )}

      {tab === 'cluster-chart' && (() => {
        // Cumulative rows
        const dates = [...new Set(growthData.flatMap(c => c.data.map(d => d.date)))].sort();
        const cumulativeRows = dates.map(date => {
          const row = { date };
          growthData.forEach(c => {
            const point = c.data.find(d => d.date === date);
            row[c.id] = point ? point.cumulative : 0;
          });
          return row;
        });

        // Activity 6h rows
        const buckets6h = [...new Set(activity6hData.flatMap(c => (c.data || []).map(d => d.bucket)))].sort();
        const activity6hRows = buckets6h.map(bucket => {
          const row = { bucket };
          activity6hData.forEach(c => {
            const point = (c.data || []).find(d => d.bucket === bucket);
            row[c.id] = point ? point.count : 0;
          });
          return row;
        });

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex gap-4 mb-3 items-center flex-wrap">
              <div className="flex gap-1 items-center">
                <span className="text-xs text-gray-500">View:</span>
                {[{ v: 'cumulative', l: 'Cumulative' }, { v: 'activity6h', l: 'Activity 6h' }].map(o => (
                  <button key={o.v} onClick={() => setClusterView(o.v)}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${clusterView === o.v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >{o.l}</button>
                ))}
              </div>
              <div className="flex gap-1 items-center">
                <span className="text-xs text-gray-500">Top:</span>
                {[3, 5, 10].map(n => (
                  <button key={n} onClick={() => setGrowthTopN(n)}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${growthTopN === n ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >{n}</button>
                ))}
              </div>
              <div className="flex gap-1 items-center">
                <span className="text-xs text-gray-500">Period:</span>
                {[7, 14, 30, 'all'].map(d => (
                  <button key={d} onClick={() => setGrowthDays(d)}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${growthDays === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >{d === 'all' ? 'All' : `${d}d`}</button>
                ))}
              </div>
              <div className="flex gap-1 items-center">
                <span className="text-xs text-gray-500">Rank:</span>
                {[{ v: 'volume', l: 'By volume' }, { v: 'recent', l: 'By recent' }].map(o => (
                  <button key={o.v} onClick={() => setChartSort(o.v)}
                    className={`px-2 py-0.5 rounded text-xs font-medium ${chartSort === o.v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >{o.l}</button>
                ))}
              </div>
            </div>
            {clusterView === 'cumulative' ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={cumulativeRows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend />
                  {growthData.map((c, i) => (
                    <Line key={c.id} type="monotone" dataKey={c.id}
                      name={c.label} stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={activity6hRows} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#6B7280' }} angle={-45} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend />
                  {activity6hData.map((c, i) => (
                    <Bar key={c.id} dataKey={c.id} name={c.label} stackId="stack"
                      fill={LINE_COLORS[i % LINE_COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        );
      })()}

      {tab === 'feed' && (
        <>
          {/* Filters */}
          <div className="flex gap-2 mb-3 flex-wrap">
            <input
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search content…"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-48"
            />
            <input
              value={handle} onChange={e => setHandle(e.target.value)}
              placeholder="Handle"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-32"
            />
            <input
              type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              title="From date"
            />
            <input
              type="date" value={to} onChange={e => setTo(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
              title="To date"
            />
            <div className="flex gap-1 items-center">
              <span className="text-xs text-gray-500">Sort:</span>
              {SORT_OPTIONS.map(o => (
                <button
                  key={o.value}
                  onClick={() => { setSort(o.value); setPage(1); }}
                  className={`px-2 py-1 rounded text-xs font-medium border ${sort === o.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >{o.label}</button>
              ))}
            </div>
            <button
              onClick={() => { setShowIrrelevant(v => !v); setPage(1); }}
              className={`ml-auto px-2 py-1 rounded text-xs font-medium border ${showIrrelevant ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300'}`}
            >Show irrelevant</button>
          </div>

          {/* Active filters */}
          <div className="flex gap-2 mb-2 flex-wrap">
            {handle && (
              <span className='inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs'>
                @{handle}
                <button onClick={() => setHandle('')} className='ml-1 font-bold hover:text-blue-900'>×</button>
              </span>
            )}
            {clusterTweetIds && (
              <span className='inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs'>
                Cluster: {activeClusterLabel.slice(0, 60)}{activeClusterLabel.length > 60 ? '...' : ''}
                <button onClick={() => { setClusterTweetIds(null); setActiveClusterLabel(''); }} className='ml-1 font-bold hover:text-blue-900'>×</button>
              </span>
            )}
          </div>

          {/* Tweet table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[150px_110px_1fr_auto] text-xs font-medium text-gray-500 uppercase px-4 py-2 border-b border-gray-200 bg-gray-50">
              <span>Posted</span><span>Handle</span><span>Content</span><span>Flags</span>
            </div>
            {tweets.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">No tweets found</div>
            ) : tweets.map(t => {
              const tweetUrl = t.tweet_id && t.author_handle
                ? `https://x.com/${t.author_handle}/status/${t.tweet_id}` : null;
              return (
                <div
                  key={t.id}
                  onClick={() => tweetUrl && window.open(tweetUrl, '_blank', 'noopener')}
                  className={`grid grid-cols-[150px_110px_1fr_auto] px-4 py-2 border-b border-gray-100 text-sm hover:bg-gray-50 cursor-pointer items-start ${t.relevant === 0 ? 'opacity-50 bg-gray-50' : ''}`}
                >
                  <span className="text-gray-500 text-xs whitespace-nowrap" title={t.crawled_at ? `Crawled: ${fmtDate(t.crawled_at, tz)}` : ''}>{fmtDate(t.tweet_created_at, tz)}</span>
                  <span className="overflow-hidden">
                    <button
                      onClick={e => { e.stopPropagation(); filterByHandle(t.author_handle || ''); }}
                      className="text-blue-600 hover:underline text-sm font-medium block max-w-full truncate"
                      title={`@${t.author_handle || '?'}`}
                    >@{t.author_handle || '?'}</button>
                  </span>
                  <span className={`break-words text-sm ${t.content ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                    {t.content ? t.content.slice(0, 200) : '[media only — no text]'}
                  </span>
                  <span className="flex gap-0.5 items-center">
                    <Flag label="C" active={!!t.processed_cluster} />
                    <Flag label="F" active={!!t.processed_facts} />
                    <Flag label="D" active={!!t.processed_dashboard} />
                    <WalterFlag value={t.processed_walter} />
                    {t.relevant === 0 && <span className="ml-1 text-[10px] text-gray-400 bg-gray-100 rounded px-1">irrelevant</span>}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center gap-4 mt-3 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default"
            >Prev</button>
            <span className="text-gray-600">Page {page} of {totalPages} ({total} tweets)</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default"
            >Next</button>
          </div>
        </>
      )}

      {tab === 'authors' && (
        <>
          {/* Sort controls */}
          <div className="flex gap-2 mb-4 items-center flex-wrap">
            <span className="text-xs text-gray-500">Sort by:</span>
            {[
              { v: 'total', l: 'Total tweets' },
              { v: 'relevant_pct', l: 'Relevance %' },
              { v: 'handle', l: 'Handle A–Z' },
            ].map(o => (
              <button key={o.v} onClick={() => setAuthorSort(o.v)}
                className={`px-2 py-1 rounded text-xs font-medium border ${authorSort === o.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              >{o.l}</button>
            ))}
            <span className="ml-auto text-xs text-gray-500">{authors.length} authors</span>
          </div>

          {/* Author cards grid */}
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {[...authors].sort((a, b) => {
              if (authorSort === 'relevant_pct') {
                const pctA = a.tweetCount > 0 ? a.relevantCount / a.tweetCount : 0;
                const pctB = b.tweetCount > 0 ? b.relevantCount / b.tweetCount : 0;
                return pctB - pctA;
              }
              if (authorSort === 'handle') return (a.handle || '').localeCompare(b.handle || '');
              return b.tweetCount - a.tweetCount;
            }).map(a => {
              const relPct = a.tweetCount > 0 ? (a.relevantCount / a.tweetCount) * 100 : 0;
              return (
                <div key={a.handle} className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <a
                      href={`https://x.com/${a.handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold text-sm"
                    >@{a.handle}</a>
                    <button
                      onClick={() => { filterByHandle(a.handle); }}
                      className="text-xs text-gray-400 hover:text-blue-600 hover:underline"
                      title="Filter feed by this handle"
                    >Feed →</button>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">{a.tweetCount}</span> tweets ·{' '}
                    <span className="font-medium">{a.relevantCount}</span> relevant
                  </div>
                  <div className="text-xs text-gray-500">
                    {relPct.toFixed(1)}% relevant
                  </div>
                  <div className="text-xs text-gray-400">
                    Last seen {relativeTime(a.lastSeen)}
                  </div>
                  {a.summary && (
                    <div className="border-t border-gray-100 pt-2 mt-1 text-xs text-gray-600 leading-relaxed">
                      {a.summary}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'clusters' && (
        <>
          <div className="text-sm text-gray-500 mb-3"><strong className="text-gray-900">{filteredClusters.length}</strong> clusters{clusterCutoff !== "all" ? " (filtered)" : ""} of <strong className="text-gray-900">{clusters.length}</strong> total</div>

          {/* Cluster controls */}
          <div className="flex gap-2 mb-3 flex-wrap items-center">
            <span className="text-xs text-gray-500">Sort:</span>
            {CLUSTER_SORT_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => setClusterSort(o.value)}
                className={`px-2 py-1 rounded text-xs font-medium border ${clusterSort === o.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              >{o.label}</button>
            ))}
            <span className="text-xs text-gray-500 ml-3">Active within:</span>
            {CUTOFF_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => setClusterCutoff(o.value)}
                className={`px-2 py-1 rounded text-xs font-medium border ${clusterCutoff === o.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              >{o.label}</button>
            ))}
          </div>

          {/* Clusters table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Cluster</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Tweets</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {filteredClusters.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No clusters found</td></tr>
                ) : filteredClusters.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => {
                      setClusterTweetIds(c.tweet_ids && c.tweet_ids.length > 0 ? c.tweet_ids : null);
                      setActiveClusterLabel(c.label || '');
                      setHandle(''); setQ(''); setPage(1); setTab('feed');
                    }}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-2 text-gray-800">{c.label}</td>
                    <td className="px-4 py-2 text-gray-600">{c.tweet_count}</td>
                    <td className="px-4 py-2 text-gray-500">{c.last_activity ? fmtDate(c.last_activity, tz) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
